<template>
    <div id="mail-preview">
        <div class="meta">
            <div>
                <strong>{{ $t('digital.email.view_item.from') }}</strong>
                {{ senderName }} <i v-if="senderEmail">&lt;{{ senderEmail }}&gt;</i>
            </div>

            <div>
                <strong>{{ $t('digital.email.view_item.subject') }}</strong>
                {{ subject === '' ? $t('digital.email.view_item.no_subject') : subject }}
            </div>

            <div class="attachments" v-if="attachments">
                <strong>{{ $t('digital.email.view_item.attachment') }}</strong>
                <div class="attachment" v-for="attachment in attachments">
                    <i class="icon">attachment</i>
                    {{ attachment }}
                </div>
            </div>
        </div>

        <div class="email-bg" :style="{ backgroundColor: mailTemplate.background_color }">
            <div class="email-body" :style="{
                fontFamily: cssSafeFontFamily(mailTemplate.font_family),
                backgroundColor: mailTemplate.content_background_color,
            }">
                <div class="email-body-header" :style="{ textAlign: mailTemplate.logo_alignment }">
                    <slot name="header"></slot>
                </div>

                <div class="email-body-content" :style="{ color: mailTemplate.content_foreground_color }">
                    <slot name="body"></slot>
                </div>

                <div class="email-support-footer"
                     :style="{
                         color: mailTemplate.content_foreground_color,
                         '--hyperlink-color': mailTemplate.content_hyperlink_color,
                     }">
                    <slot name="footer">
                        <i18n path="digital.email.support_message" tag="span">
                            <template #email>
                                <u :style="{ color: mailTemplate.content_hyperlink_color }">info@gifty.nl</u>
                            </template>
                            <template #orderNumber>2025324503</template>
                        </i18n>
                    </slot>
                </div>

                <div class="footer-logo" :style="{ '--divider-color': mailTemplate.divider_color }">
                    <img v-if="backgroundRequiresLightLogo" src="../../../../../images/email/gifty-logo-light.png" alt="Gifty">
                    <img v-else src="../../../../../images/email/gifty-logo.png" alt="Gifty">
                </div>
            </div>

            <div class="email-footer" :style="{ color: footerTextColor }">
                {{ $t('digital.email.company_footer', { email: 'info@gifty.nl', terms: $t('digital.email.terms_conditions'), privacy: $t('digital.email.privacy_policy') }) }}
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'EmailViewItem',
    props: {
        senderName: {
            type: String,
        },
        senderEmail: {
            type: String,
        },
        subject: {
            type: String,
        },
        attachments: {
            type: Array,
        },
        mailTemplate: {
            type: Object,
            required: true,
        },
    },

    computed: {
        footerTextColor() {
            const backgroundColor = this.mailTemplate.background_color;

            // Use default footer color for default background
            if (backgroundColor.toLowerCase() === '#eceff0') {
                return '#a7a7a7';
            }

            return this.getContrastingTextColor(backgroundColor);
        },

        backgroundRequiresLightLogo() {
            const hex = this.mailTemplate.content_background_color;
            if (!hex) return false;

            const getLuminance = (hexColor) => {
                let color = hexColor.replace("#", "");
                if (color.length === 3) {
                    color = color.split("").map(c => c + c).join("");
                }

                const r = parseInt(color.substring(0, 2), 16);
                const g = parseInt(color.substring(2, 4), 16);
                const b = parseInt(color.substring(4, 6), 16);

                return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
            };

            return getLuminance(hex) < 0.5;
        },
    },

    methods: {
        cssSafeFontFamily(value) {
            const fontFamilyCSSMapping = {
                'arial': 'Arial, sans-serif',
                'helvetica': 'Helvetica, Arial, sans-serif',
                'verdana': 'Verdana, Geneva, sans-serif',
                'tahoma': 'Tahoma, Geneva, sans-serif',
                'trebuchet_ms': '"Trebuchet MS", Helvetica, sans-serif',
                'times_new_roman': '"Times New Roman", Times, serif',
                'georgia': 'Georgia, "Times New Roman", serif',
                'garamond': 'Garamond, "Times New Roman", serif',
                'courier_new': '"Courier New", Courier, monospace',
                'brush_script_mt': '"Brush Script MT", cursive'
            };

            return fontFamilyCSSMapping[value] || 'Arial, sans-serif';
        },

        getContrastingTextColor(backgroundColor) {
            if (!backgroundColor) return '#000000';

            const getLuminance = (hex) => {
                let color = hex.replace("#", "");
                if (color.length === 3) {
                    color = color.split("").map(c => c + c).join("");
                }

                const r = parseInt(color.substring(0, 2), 16);
                const g = parseInt(color.substring(2, 4), 16);
                const b = parseInt(color.substring(4, 6), 16);

                const rsRGB = r / 255;
                const gsRGB = g / 255;
                const bsRGB = b / 255;

                const rLum = rsRGB <= 0.03928 ? rsRGB / 12.92 : Math.pow((rsRGB + 0.055) / 1.055, 2.4);
                const gLum = gsRGB <= 0.03928 ? gsRGB / 12.92 : Math.pow((gsRGB + 0.055) / 1.055, 2.4);
                const bLum = bsRGB <= 0.03928 ? bsRGB / 12.92 : Math.pow((bsRGB + 0.055) / 1.055, 2.4);

                return 0.2126 * rLum + 0.7152 * gLum + 0.0722 * bLum;
            };

            const calculateContrastRatio = (color1, color2) => {
                const lum1 = getLuminance(color1);
                const lum2 = getLuminance(color2);
                const brightest = Math.max(lum1, lum2);
                const darkest = Math.min(lum1, lum2);
                return (brightest + 0.05) / (darkest + 0.05);
            };

            const contrastWithBlack = calculateContrastRatio(backgroundColor, '#000000');
            const contrastWithWhite = calculateContrastRatio(backgroundColor, '#ffffff');

            return contrastWithBlack > contrastWithWhite ? '#000000' : '#ffffff';
        },
    },
}
</script>

<style scoped lang="scss">
#mail-preview {
    background-color: #ECEFF0;
    font-size: 0.875rem;
    color: #6A7272;
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.email-bg {
    padding: 2.5rem;
    overflow-y: scroll;
    flex: 1;
    flex-grow: 2;
    background-color: #ECEFF0;
}

.email-body {
    max-width: 31.25rem;
    margin: 0 auto;
    padding: 0;
    background-color: #fff;

    .email-body-header {
        padding: 2rem 2.1875rem 0 2.1875rem;
        text-align: center;

        img {
            max-width: 128px;
            max-height: 74px;
        }
    }

    .email-body-content {
        padding: 1rem 2.1875rem;
        white-space: pre-wrap;
        overflow-wrap: break-word;
    }

    .email-support-footer {
        padding: 1.325rem 2.297rem;

        u {
            color: var(--hyperlink-color, #000);
        }
    }

    .footer-logo {
        text-align: center;
        padding-bottom: 30px;
        padding-top: 30px;
        border-top: 0.063rem solid var(--divider-color, #F9F9F9);

        img {
            width: 91px;
            height: 36px;
        }
    }
}

.email-footer {
    text-align: center;
    color: #A7A7A7;
    font-size: 0.688rem;
    margin-top: 0.625rem;
}

.meta {
    font-size: 0.875rem;
    padding: 0.8rem 1rem;
    background-color: #fff;

    .attachments {
        display: flex;
        align-items: flex-start;
        flex-wrap: wrap;

        strong {
            margin-right: 0.2rem;
        }

        .attachment {
            background-color: #F5F5F5;
            color: #4A4A4A;
            padding: 0 0.3rem;
            font-size: 0.75rem;
            border-radius: 0.188rem;
            margin: 0.1rem 0.2rem 0.1rem 0;
            flex: 0 0 auto;
            white-space: nowrap;

            .icon {
                position: relative;
                top: 0.15rem;
                font-size: 0.8rem;
            }
        }
    }
}
</style>
